import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays } from 'date-fns';
import { ShutdownRitual } from '@/components/ShutdownRitual';
import { useBlocks, formatDateKey } from '@/hooks/useBlocks';
import { useRevisionLog } from '@/hooks/useRevisionLog';
import { useTasks } from '@/hooks/useTasks';
import { RitualState } from '@/types';

export function ShutdownRitualPage() {
  const navigate = useNavigate();
  const today = new Date();
  const todayKey = formatDateKey(today);
  const tomorrowKey = formatDateKey(addDays(today, 1));

  // Fetch today's blocks for the ritual
  const { blocks, createBlock, updateBlock } = useBlocks({
    date: todayKey,
    includeRevisions: false,
  });

  // Get createBlock for tomorrow (we need to create blocks with tomorrow's date)
  const { createBlock: createTomorrowBlock } = useBlocks({
    date: tomorrowKey,
    includeRevisions: false,
  });

  // Fetch today's revision summary
  const { getRevisionSummary } = useRevisionLog(todayKey);
  const revisionSummary = getRevisionSummary();

  // For creating tasks from captured thoughts
  const { createTask } = useTasks();

  const handleComplete = useCallback(
    async (state: RitualState) => {
      console.log('Ritual completed:', state);

      try {
        // 1. Create tomorrow's planned blocks
        for (const block of state.tomorrowBlocks) {
          await createTomorrowBlock({
            date: tomorrowKey,
            startTime: block.startTime,
            duration: block.duration,
            title: block.title,
            notes: block.notes || '',
            categoryId: block.categoryId || '',
            color: block.color,
            status: 'planned',
          });
        }

        // 2. Handle carry-over blocks (copy to tomorrow)
        for (const [blockId, decision] of Object.entries(state.unfinishedDecisions)) {
          if (decision === 'carry-over') {
            const originalBlock = blocks.find((b) => b.id === blockId);
            if (originalBlock) {
              // Create copy for tomorrow
              await createTomorrowBlock({
                date: tomorrowKey,
                startTime: originalBlock.startTime,
                duration: originalBlock.duration,
                title: originalBlock.title,
                notes: originalBlock.notes || '',
                categoryId: originalBlock.categoryId || '',
                color: originalBlock.color,
                status: 'planned',
              });
              // Mark original as cancelled
              await updateBlock(blockId, { status: 'cancelled' });
            }
          } else if (decision === 'reschedule') {
            // Leave block as-is - user will manually reschedule later
            // Could potentially mark with a flag for the task list
          } else if (decision === 'delete') {
            // Mark as cancelled
            await updateBlock(blockId, { status: 'cancelled' });
          }
        }

        // 3. Create tasks from captured thoughts
        for (const taskTitle of state.capturedTasks) {
          await createTask({
            title: taskTitle,
            priority: 'medium',
          });
        }

        console.log('Shutdown ritual data saved successfully');
      } catch (error) {
        console.error('Error saving shutdown ritual data:', error);
      }

      // Navigate back after saving
      navigate('/');
    },
    [blocks, createTomorrowBlock, updateBlock, createTask, tomorrowKey, navigate]
  );

  const handleSkip = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleViewRevisionHistory = useCallback(() => {
    // Placeholder for viewing full revision history
    // Could navigate to a dedicated history page or open a modal
    console.log('View full revision history');
  }, []);

  return (
    <ShutdownRitual
      todayBlocks={blocks}
      revisionSummary={revisionSummary}
      onComplete={handleComplete}
      onSkip={handleSkip}
      onViewRevisionHistory={handleViewRevisionHistory}
    />
  );
}

export default ShutdownRitualPage;
